import type { FileDTO } from "../../shared/types";
import { Icon, type IconName } from "./Icon";
import { IconButton } from "./IconButton";
import { TextLink } from "./TextLink";
import { Badge } from "./ui";

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const isImage = (f: Pick<FileDTO, "mime">) => f.mime.startsWith("image/") && f.mime !== "image/svg+xml";
export const iconFor = (mime: string): IconName =>
  mime.startsWith("image/") ? "image" : mime.includes("sheet") || mime === "text/csv" ? "sheet" : mime === "application/pdf" || mime.includes("word") || mime.startsWith("text/") ? "file" : "paperclip";

export const fileUrl = (id: string, inline = false) => `/api/files/${id}${inline ? "?inline=1" : ""}`;

/** Who may read a file beside its owner (security plan D16), as a badge: its scope, everyone, the people it is shared with, or nobody else. */
export function FileSharingBadge({ file }: { file: Pick<FileDTO, "visibility" | "sharedWith" | "subjectId" | "subjectName"> }) {
  if (file.visibility === "everyone") return <Badge tone="blue">Everyone</Badge>;
  const shared = file.sharedWith > 0 ? `shared with ${file.sharedWith}` : "";
  if (file.subjectId) return <Badge tone="teal">{[`Scope${file.subjectName ? ` · ${file.subjectName}` : ""}`, shared].filter(Boolean).join(", ")}</Badge>;
  return shared ? <Badge tone="violet">{shared[0]!.toUpperCase() + shared.slice(1)}</Badge> : <Badge tone="zinc">Private</Badge>;
}

/** A file that is not shared with whoever is looking: its name and size stay, nothing opens (an admin reading someone's chat). */
const closed = (file: { canOpen?: boolean }) => file.canOpen === false;

/** Compact chip used in the composer and message bubbles. */
export function FileChip({ file, onRemove, busy }: { file: Pick<FileDTO, "name" | "mime" | "size"> & { id?: string; canOpen?: boolean }; onRemove?: () => void; busy?: boolean }) {
  const body = (
    <>
      <Icon name={iconFor(file.mime)} className="size-3.5 shrink-0 text-faint" />
      <span className="max-w-[12rem] truncate">{file.name}</span>
      <span className="text-faint">{busy ? "uploading…" : formatBytes(file.size)}</span>
    </>
  );
  return (
    <span className="inline-flex items-center gap-1.5 card px-2 py-1 text-xs">
      {file.id && !closed(file) ? (
        <TextLink quiet newTab href={fileUrl(file.id)} className="inline-flex items-center gap-1.5">
          {body}
        </TextLink>
      ) : (
        body
      )}
      {onRemove && (
        <IconButton size="sm" fill="none" label={`Remove ${file.name}`} onClick={onRemove} className="-my-1 -mr-1.5 ml-0.5">
          <Icon name="close" className="size-3" />
        </IconButton>
      )}
    </span>
  );
}

/** Rich card for files produced by agents or attached to messages: image thumbnail, PDF link, download. */
export function FileCard({ file }: { file: FileDTO }) {
  if (closed(file)) {
    return (
      <div className="flex items-center gap-2 card px-3 py-2 text-sm">
        <Icon name={iconFor(file.mime)} className="size-3.5 shrink-0 text-faint" />
        <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
        <span className="shrink-0 text-xs text-faint">{formatBytes(file.size)} · not shared with you</span>
      </div>
    );
  }
  return (
    <div className="overflow-hidden card">
      {isImage(file) && (
        <a href={fileUrl(file.id, true)} target="_blank" rel="noopener">
          <img src={fileUrl(file.id, true)} alt={file.name} loading="lazy" className="max-h-72 w-full bg-fill object-contain" />
        </a>
      )}
      <div className="flex items-center gap-2 px-3 py-2 text-sm">
        <Icon name={iconFor(file.mime)} className="size-3.5 shrink-0 text-faint" />
        <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
        <span className="shrink-0 text-xs text-faint">{formatBytes(file.size)}</span>
        {file.mime === "application/pdf" && (
          <TextLink newTab href={fileUrl(file.id, true)} className="shrink-0 text-xs">
            open
          </TextLink>
        )}
        <TextLink href={fileUrl(file.id)} className="shrink-0 text-xs">
          Download
        </TextLink>
      </div>
    </div>
  );
}
